/**
 * 보고 문서의 글을 쓴다 — 명사형 제목, □ 요약, □ 확인 필요 사항, □ 향후 조치(안), 그래프마다 결론 한 줄.
 * 기호와 순서는 렌더러가 붙인다. 여기서는 칸별 문장만.
 *
 * 항목마다 근거에 없는 숫자를 쓰거나 개조식 검사에 걸리면 그 항목만 버린다. 요약이 하나도 안 남으면
 * 작성 결과를 쓰지 않는다 — 부르는 쪽이 정해 둔 제목과 직전 답 요지로 떨어진다.
 */
import { unsupportedNumbers } from './experts';
import type { ReportChart, ResultAxis } from './files';
import { namesIn } from './listTables';
import type { Chat } from './plan';
import {
  GENERAL_REPORT_STYLE, ISSUE_REPORT_STYLE, issueLint, NOTICE_STYLE, noticeLint, REPORT_STYLE, reportLint,
} from './writing';

const BRIEF_PROMPT = `사무직 동료가 물었고({question}) 그것을 보고 문서로 청했다. 아래 근거로 문서의 칸을 채운다.
- title: 문서 제목. 명사형 주제(예: 10월 자체점검 진행 현황). 결론 문장도 「현황 보고서」 같은 빈 이름도 아니다.
- summary: 요약 2~3항목. 첫 항목은 질문에 대한 판단, 다음 항목은 그 판단의 근거 숫자(비율 포함).
- checks: 확인이 필요한 사항 0~3항목. 기한 경과·미달·예외 건과, 자료가 없어 판단할 수 없는 것.
- actions: 향후 조치(안) 0~3항목. 근거에서 바로 나오는 확인·조치만.
- charts: 그래프 값의 그래프마다 그 그래프가 보여 주는 결론 한 줄, 그래프 차례대로. 이름과 숫자가 든 줄(예: 이후 예정 336건 중 ○○현장 120건).
  막대 값을 늘어놓지 말고 합계로 쓴다. 숫자는 넷까지(예: 경과는 ○○ 2개 현장 41건, 나머지 5개 현장 73건은 예정일 이내).
  그래프 합계 줄에 묶음 이름(「모두 ○○」)이 있으면 그 이름을 쓴다. 「분포」「나타남」처럼 아무것도 말하지 않는 말로 끝내지 마라.
- 요약에 쓴 것을 checks 에 되풀이하지 마라. 아래 「문서에 이미 쓴 줄」도 되풀이하지 마라 — 그래프 결론은 그 그래프의 값에서 새로 읽히는 것만.
- 직전 답에만 있고 아래 근거·계산 요약·그래프 값·갈래 결과에 없는 숫자는 쓰지 마라 — 대조할 데가 없다.
- 직전 답의 「계산하지 못한 갈래」는 문서에 쓰지 마라 — 자료가 없는 것이 아니라 우리가 계산하지 못한 것이다.
- 근거에 없는 숫자를 쓰지 마라. 비율은 근거에 적힌 것만 옮긴다. 새로 셈해도 되는 것은 근거의 두 숫자를 뺀 차이뿐이다.

${REPORT_STYLE}

JSON 하나만 낸다: {"title":"…","summary":["…"],"checks":["…"],"actions":["…"],"charts":["…"]}

직전 답:
{answer}

표·비율 근거:
{facts}

문서에 이미 쓴 줄:
{stated}

갈래 결과(같은 자료를 갈래마다 코드가 센 값):
{axes}

계산 요약:
{summary}

그래프 값:
{charts}`;

/** 점검 지시문의 뒤쪽(되풀이·근거 규칙, 글 규칙, JSON, 근거 자리). 다른 문서 종류는 앞쪽과 글 규칙만 바꾼다. */
const BRIEF_TAIL = BRIEF_PROMPT.slice(BRIEF_PROMPT.indexOf('- 요약에 쓴 것을'));

/** 점검이 아닌 업무의 보고 문서. 사용자가 말한 주제가 제목이 된다(「잔여 연차 소진 촉진건으로」를 무시했다, 실측 2026-10-26). */
const GENERAL_PROMPT = `사무직 동료가 물었고({question}) 그것을 보고 문서로 청했다. 아래 근거로 문서의 칸을 채운다.
- title: 문서 제목. 명사형 주제. 사용자가 주제를 말했으면(「○○건으로」) 그 주제를 제목으로 쓴다(예: ○○ 계획(안), ○○ 현황).
  결론 문장도 「현황 보고서」 같은 빈 이름도 아니다.
- summary: 요약 2~3항목. 첫 항목은 질문에 대한 판단(숫자가 든 결론), 다음 항목은 그 판단의 근거 숫자(비율 포함).
- checks: 확인이 필요한 사항 0~2항목. 근거에 나온 예외나 쏠림만. 없으면 빈 목록.
- actions: 향후 조치(안) 1~3항목. 근거에서 바로 나오는 조치만.
- charts: 그래프 값의 그래프마다 그 그래프가 보여 주는 결론 한 줄, 그래프 차례대로. 이름과 숫자가 든 줄.
  막대 값을 늘어놓지 말고 위 몇 개와 합계로 쓴다. 숫자는 넷까지. 「분포」「나타남」처럼 아무것도 말하지 않는 말로 끝내지 마라.
${BRIEF_TAIL.replace(REPORT_STYLE, GENERAL_REPORT_STYLE)}`;

/**
 * 공지(사내 게시용 안내문). 확인 필요 사항 칸은 없다. 직원에게 내부 검토를 알리지 않는다. summary가 앞 항목(목적·대상), actions가
 * 뒤 항목(할 일과 기한)이고 문서가 번호를 붙인다(국가직 팀장 검토: 게시물 꼴과 경어). 신청 방법은 청했을 때만 쓴다(사용자 확인,
 * 2026-10-27: 연차 공지에 신청 경로를 넣지 않는다).
 */
const NOTICE_PROMPT = `사무직 동료가 물었고({question}) 그것을 사내 게시용 공지 문서로 청했다. 아래 근거로 공지의 항목을 채운다.
- title: 「(기간) ○○ 안내」 꼴의 명사형 제목.
- summary: 앞 항목 2개. 「목적: …」(무엇을 왜 안내하는지), 「대상: …」(누가 대상이고 몇 명인지).
- checks: 빈 목록.
- actions: 뒤 항목 1~3개. 직원이 할 일과 그 기한(「사용 계획 제출: …」, 「사용 기한: …」).
- charts: 그래프가 있으면 그래프마다 결론 한 줄, 그래프 차례대로. 없으면 빈 목록.
${BRIEF_TAIL.replace(REPORT_STYLE, NOTICE_STYLE)}`;

/**
 * 발견된 문제가 남아 있는 자료(결함, 고장, 민원)의 보고 문서. 근거는 코드가 센 긴급도, 처리 상태, 쏠림, 반복이다(`issues.ts`).
 * 점검 지시문으로 쓰면 「예정일 경과 102건 점검 완료」가 조치로 나갔다(실제 테넌트, 2026-10-27).
 */
const ISSUE_PROMPT = `사무직 동료가 물었고({question}) 그것을 보고 문서로 청했다. 아래 근거로 문서의 칸을 채운다.
- title: 문서 제목. 자료의 달로 시작하는 명사형 주제(예: 10월 ○○ 결함 처리 현황). 사용자가 주제나 강조점(「특이사항 중심」)을
  말했으면 제목과 요약에 살린다.
  결론 문장도 「현황 보고서」 같은 빈 이름도 아니다.
- summary: 요약 2~3항목. 첫 항목은 급한 문제의 건수와 처리 상태(숫자가 든 결론), 다음 항목은 쏠림과 반복(비율 포함).
- checks: 확인이 필요한 사항 0~2항목. 근거에 나온 쏠림이나 비어 있는 처리 기록만. 없으면 빈 목록.
- actions: 향후 조치(안) 1~3항목. 급한 것부터 처리 일과 기한, 담당.
- charts: 그래프 값의 그래프마다 그 그래프가 보여 주는 결론 한 줄, 그래프 차례대로. 이름과 숫자가 든 줄.
  막대 값을 늘어놓지 말고 위 몇 개와 합계로 쓴다. 숫자는 넷까지. 「분포」「나타남」처럼 아무것도 말하지 않는 말로 끝내지 마라.
${BRIEF_TAIL.replace(REPORT_STYLE, ISSUE_REPORT_STYLE)}`;

/** 문서 종류: 점검(status, 〈표 1〉·경과가 있는 판), 그 밖의 업무 보고(general), 공지(notice), 열린 문제(issues). */
export type BriefKind = 'status' | 'general' | 'notice' | 'issues';

const PROMPTS: Record<BriefKind, string> = {
  status: BRIEF_PROMPT, general: GENERAL_PROMPT, notice: NOTICE_PROMPT, issues: ISSUE_PROMPT,
};

/**
 * 줄이 말하는 달 — 「8~10월」은 8·9·10, 「9·10월」은 9·10, 「10월」은 10. 날짜('26.10.26.)는 달로 보지 않는다.
 */
export function monthsIn(line: string): number[] {
  const found: number[] = [];
  const ranged = line.replace(/(?<![\d.'])(\d{1,2})\s*([~·,])\s*(\d{1,2})\s*월/g, (_, left: string, mark: string, right: string) => {
    const [from, to] = [Number(left), Number(right)];
    if (mark === '~' && from <= to) for (let month = from; month <= to; month += 1) found.push(month);
    else found.push(from, to);
    return ' ';
  });
  ranged.replace(/(?<![\d.'])(\d{1,2})\s*월/g, (whole, month: string) => {
    found.push(Number(month));
    return whole;
  });
  return found.filter((month) => month >= 1 && month <= 12);
}

/** 갈래 결과 중 프롬프트에 싣는 만큼 — 모델이 본 숫자만 근거이고 각주의 출처다. */
export function axisText(one: ResultAxis): string {
  // 빈 값(undefined)은 stringify가 undefined를 돌려줘 slice에서 멈춘다(실측 2026-10-26 05:35, 2턴 보고서).
  return JSON.stringify(one.value ?? null).slice(0, 1500);
}

/** 보고 문서의 칸. 그래프 문장은 그래프 차례대로, 못 쓴 것은 빈 문자열이다. */
export type Brief = { title: string; summary: string[]; checks: string[]; actions: string[]; charts?: string[] };

/** 달·날짜 범주(「'26.9월」, 「9월」, 「2026-09」). */
const TIME_CATEGORY = /\d\s*월|^'\d{2}\.|^(?:19|20)\d{2}/;

/**
 * 그래프 값 글. totals면 계열 하나인 막대(달이 아닌 범주)에 위 3개 합과, 그림이 전체를 그렸을 때 합계를 붙인다. 점검이 아닌
 * 문서만 쓴다(점검 지시문의 근거는 예전 그대로, 독립 검토). 「위 몇 개와 합계로」 쓴 부서별 결론이 근거 없음으로 빠졌다(실측 표로
 * 그린 판, 2026-10-26). 위 몇 개만 그린 그림(「상위 12개 부서」, 「3명 이상 부서」)의 합은 전체 합계가 아니다.
 */
export function chartText(charts: ReportChart[], totals = false): string {
  return charts.map((chart) => {
    const values = `${chart.title}: ${chart.categories.map((category, index) => (
      `${category} ${chart.series.map((series) => `${series.name} ${series.values[index] ?? '없음'}`).join(' / ')}`
    )).join(', ')}`;
    const [only] = chart.series;
    if (totals && !chart.kind && chart.series.length === 1 && only && chart.categories.length >= 3
      && !chart.categories.some((category) => TIME_CATEGORY.test(category))) {
      const sorted = only.values.map((value) => value ?? 0).sort((left, right) => right - left);
      const sum = (list: number[]) => Math.round(list.reduce((total, value) => total + value, 0) * 100) / 100;
      const partial = /상위\s*\d+|\d+명\s*이상|제외\)$/.test(chart.title);
      return `${values}\n${chart.title} ${partial ? '' : `합계: ${sum(sorted).toLocaleString('en-US')}, `}`
        + `위 3개 합: ${sum(sorted.slice(0, 3)).toLocaleString('en-US')}`;
    }
    // 쌓은 막대는 계열 합계를 준다 — 결론 줄이 막대 값 일곱 개를 다 늘어놓았다(7차 검토).
    if (chart.kind !== 'stack') return values;
    const sums = chart.series.map((series) => {
      const filled = chart.categories.filter((_, index) => (series.values[index] ?? 0) > 0);
      const total = series.values.reduce<number>((sum, value) => sum + (value && value > 0 ? value : 0), 0);
      // 묶음 이름까지 — 그림 결론이 「41건은 2곳에 분포」로 현장 이름을 잃었다(9차 검토).
      const families = Array.from(new Set(filled.map((category) => category.replace(/\s*\(.*\)\s*$/, '').trim())));
      const family = filled.length > 1 && families.length === 1 ? `, 모두 ${families[0]}` : '';
      return `${series.name} ${total.toLocaleString('en-US')}건(${filled.length}곳${family})`;
    });
    return `${values}\n${chart.title} 합계: ${sums.join(', ')}`;
  }).join('\n') || '없음';
}

/** 근거 숫자 둘의 차이. 「9월보다 434건 적다」는 지어낸 숫자가 아니다. */
function differences(grounds: unknown[]): number[] {
  const known: number[] = [];
  const collect = (value: unknown): void => {
    if (typeof value === 'number' && Number.isFinite(value)) known.push(value);
    else if (typeof value === 'string') {
      (value.replace(/(\d),(\d{3})/g, '$1$2').match(/\d+(?:\.\d+)?/g) ?? []).forEach((one) => known.push(Number(one)));
    } else if (Array.isArray(value)) value.forEach(collect);
    else if (value && typeof value === 'object') Object.values(value as Record<string, unknown>).forEach(collect);
  };
  collect(grounds);
  const numbers = Array.from(new Set(known)).slice(0, 80);
  const found: number[] = [];
  numbers.forEach((left) => numbers.forEach((right) => { if (left > right) found.push(left - right); }));
  return found;
}

/** 견주는 말. 이런 문장에서만 근거 두 숫자의 차이를 받아 준다. */
const COMPARING = /보다|차이|늘|줄|감소|증가|많|적/;

/**
 * 문장마다 근거에 없는 숫자를 본다. 근거 숫자가 많으면 그 차이가 웬만한 작은 수를 다 덮는다 —
 * 막대 값 스무 개면 1~100 의 셋 중 둘이 통과했다(검토, 2026-10-23). 그래서 차이는 견주는 문장에서만,
 * 비율(%)은 차이로 받지 않는다.
 */
function grounded(text: string, grounds: unknown[]): boolean {
  const known = [{ axis: '근거', value: grounds }];
  const gaps = [{ axis: '차이', value: differences(grounds) }];
  return text.split(/[.!?]\s+|\n+/).every((sentence) => {
    const stray = unsupportedNumbers(sentence, known);
    if (!stray.length) return true;
    if (!COMPARING.test(sentence)) return false;
    const plain = sentence.replace(/(\d),(\d{3})/g, '$1$2');
    return stray.every((number) => !new RegExp(`${number.replace('.', '\\.')}\\s*%`).test(plain)
      && !unsupportedNumbers(number, gaps).length);
  });
}

export async function writeBrief(input: {
  question: string;
  answer: string;
  summary: unknown;
  charts: ReportChart[];
  /** 코드가 센 표·비율 — 모델이 비율을 지어내지 않고 옮기게 한다. */
  facts?: string[];
  /** 코드가 문서에 이미 쓴 줄(〈표 2〉 위 쏠림 등). 그림 결론이 이것을 되풀이하면 뺀다. */
  stated?: string[];
  /** 갈래 계산 결과 — 코드가 센 값이라 근거로 받는다. */
  axes?: ResultAxis[];
  /** 문서가 말해도 되는 달(표에 든 달). 다른 달을 말하는 줄은 싣지 않는다 — 10월 판에 「8~10월 조회분」이 실렸다. */
  months?: number[];
  /**
   * 〈표 1〉 줄마다의 전체 건수. 「점검 대상·전체 N건」이 이 가운데 없으면 기준이 다른 수다: 날짜 칸으로 25일까지 자른
   * 갈래 값이 「점검 대상 2,009건 중 완료 1,949건」으로 〈표 1〉(2,348건)과 같은 이름으로 실렸다(2026-10-25 10:23).
   */
  totals?: number[];
  /**
   * 〈표 1〉 줄마다 끝낸 건수. 「○○ 없음 N건」이 그 달 끝낸 건보다 많으면 아직 안 한 행이 섞인 수다: 10월 전체 2,348건에서
   * 센 「고객 서명 없음 2,206건」이 실렸다(끝낸 건은 1,952건, 2026-10-25 10:51).
   */
  done?: { label: string; count: number }[];
  /** 문서 종류. 없으면 점검 보고서 지시문이다. */
  kind?: BriefKind;
  /** 사용자가 한 말(앞 턴 질문·과제 목표). titleMonths가 없을 때 제목에 써도 되는 달을 여기서 찾는다. */
  asked?: string;
  /** 점검이 아닌 문서의 제목에 써도 되는 달(사용자가 말한 달, 과제 기간, 표의 달, 다음 달부터 올해 끝 달). */
  titleMonths?: number[];
  /** 문서에 쓰면 안 되는 말(공지의 사람 이름). 든 줄은 버린다. */
  forbidden?: string[];
  chat: Chat;
}): Promise<Brief | undefined> {
  const summaryText = JSON.stringify(input.summary ?? null).slice(0, 2000);
  const facts = input.facts ?? [];
  const totals = !!input.kind && input.kind !== 'status';
  const prompt = PROMPTS[input.kind ?? 'status']
    .replace('{question}', () => input.question)
    .replace('{answer}', () => input.answer.slice(0, 3000) || '없음')
    .replace('{facts}', () => (facts.length ? facts.map((one) => `- ${one}`).join('\n') : '없음'))
    .replace('{stated}', () => (input.stated?.length ? input.stated.map((one) => `- ${one}`).join('\n') : '없음'))
    .replace('{axes}', () => (input.axes?.length
      ? input.axes.map((one) => `- ${one.axis}: ${axisText(one)}`).join('\n') : '없음'))
    .replace('{summary}', () => summaryText)
    .replace('{charts}', () => chartText(input.charts, totals));
  // 그림 합계 줄(41건·2곳)도 근거다 — 합계로 쓰라 해 놓고 버렸다.
  // 근거는 코드가 센 값뿐이다 — 채팅 답 글은 넣지 않는다. 답에만 있던 「고객싸인 없음 1,810건」은 문서에서
  // 대조할 데가 없었다(7차 검토, 2026-10-24).
  // 갈래 값은 프롬프트에 실은 만큼만 근거다 — 118KB 현장·호기 갈래 깊숙이 103이 우연히 있어 「103건」 줄이
  // 통과했다(2026-10-25, 07:15 다시 그리기).
  const grounds = [input.summary, input.charts.map((chart) => chart.series), facts, chartText(input.charts, totals),
    (input.axes ?? []).map(axisText)];
  const first = await attempt(prompt, input, grounds);
  // 요약 항목을 하나라도 버렸으면 다시 쓰게 한다 — 판단 불가 줄 같은 요약이 빠지면 질문에 답하지 못한다(3차 검토).
  // 점검이 아닌 문서의 조치에 기한이 하나도 없을 때도 다시 쓰게 한다.
  if (!first.rejected.length || (first.brief && !first.summaryLost && !first.actionsLost)) return first.brief;
  // 못 쓴 까닭을 알려 주고 한 번만 다시 — 버린 채로 두면 제목이 「ERP 현황 보고서」로 떨어진다(실제 테넌트, 2026-10-23).
  // 공지는 경어로 쓰므로 까닭도 다르다(「~습니다」를 버린 까닭으로 알리면 경어를 버린다).
  const why = input.kind === 'notice'
    ? '경어가 아니거나(「~해요」「~하세요」), 내부 검토 말(「판단 불가」), 화면 안내, 자기 보증, 8자리 날짜를 썼거나'
    : `개조식이 아니거나(「~다」「~습니다」, 화면 안내, 자기 보증, 8자리 날짜, 상태 코드)${input.kind === 'issues'
      ? ', 이미 찾은 문제에 점검 미실시의 말(미실시, 예정일 경과, 점검 완료)을 썼거나' : ''}`;
  const again = await attempt(`${prompt}

직전에 쓴 것 가운데 아래 항목은 ${why}
근거에 없는 숫자를 써서 쓸 수 없다. 규칙대로 전체를 다시 써라.
${first.rejected.map((one) => `- ${one}`).join('\n')}`, input, grounds);
  // 기한만 빠졌던 글은 처음 글을 두고 다시 쓴 글에서 기한이 붙은 조치만 가져온다. 다시 쓴 글이 요약을 잃고 요청 사항이
  // 0개인 공지가 나갔다(독립 검토).
  if (first.brief && !first.summaryLost && first.actionsLost) {
    const retried = again.brief?.actions ?? [];
    const dated = retried.filter((one) => daysIn(one).length);
    // 기한 붙은 조치가 없으면 처음 조치를, 처음 조치가 다 버려졌으면 다시 쓴 조치를 쓴다(요청 사항 없는 공지보다 낫다).
    const actions = dated.length ? dated : first.brief.actions.length ? first.brief.actions : retried;
    return { ...first.brief, actions };
  }
  // 다시 쓴 글이 조치를 잃었으면 처음 글의 쓸 수 있던 조치를 잇는다(첫 글이 제목 때문에 버려진 경우도). 점검이 아닌 문서만:
  // 점검 문서는 예전처럼 다시 쓴 글 그대로다(독립 재검토).
  const earlier = first.brief?.actions ?? first.actions ?? [];
  return totals && again.brief && !again.brief.actions.length && earlier.length ? { ...again.brief, actions: earlier } : again.brief;
}

/**
 * 버리지 않고 고치는 것 — 낱말 하나 때문에 판단 불가 문장까지 사라졌다(국가직 팀장 3차 검토).
 * 「결과 코드」→「결과 상태값」, 「2026년 10월 31일」→「'26.10.31.」, 제목에 있는 달을 항목 앞에서 뺀다.
 * 여러 달 판(keepMonth)은 달을 남긴다 — 「점검 대상 2,348건 중 완료 1,952건(83.1%), 25일 현재」가 9월 것인지
 * 10월 것인지 안 보였다(2026-10-25 09:14).
 */
function tidy(text: string, title: string, keepMonth = false, dates = false): string {
  let said = text.trim()
    // 날짜 앞 둥근 따옴표(’26.12.31.)는 곧은 따옴표로 맞춘다. 다른 날짜와 꼴이 달랐다(실측 2026-10-26 15:30).
    .replace(/[‘’`](?=\d{2}\.\d{1,2}\.)/g, "'");
  // 점검이 아닌 문서는 「2026.11.30.」, 「2026-11-30」, 「2026. 11. 30.」도 「'26.11.30.」로 맞춘다. 꼴이 달라 기한 후보와 대조되지
  // 않아 공지의 요청 사항이 빠졌다(실측 2026-10-26 16:40). 점검 문서는 예전 그대로 둔다(독립 재검토).
  if (dates) {
    said = said.replace(/(?<![\d'])(?:19|20)(\d{2})\s*[.-]\s*(\d{1,2})\s*[.-]\s*(\d{1,2})(?:\s*\.|(?!\d))/g,
      (_, year: string, month: string, day: string) => `'${year}.${Number(month)}.${Number(day)}.`);
  }
  said = said.replace(/결과\s*코드/g, '결과 상태값').replace(/상태\s*코드/g, '상태값')
    .replace(/(?:19|20)(\d{2})년\s*(\d{1,2})월\s*(\d{1,2})일/g, "'$1.$2.$3.");
  // 해 없는 「10월 30일」도 점검이 아닌 문서에서는 올해 날짜 꼴로 맞춘다. 그 꼴로 쓴 공지 기한이 기한 후보 대조를 비껴갔다(실측
  // 2026-10-26 18:12). 내년 날짜를 해 없이 쓰면 올해로 읽혀 후보와 맞지 않으니 다시 쓰게 된다.
  if (dates) {
    const year = String(new Date().getFullYear()).slice(2);
    said = said.replace(/(?<![\d'.])(1[0-2]|0?[1-9])월\s*([12]\d|3[01]|0?[1-9])일/g,
      (_, month: string, day: string) => `'${year}.${Number(month)}.${Number(day)}.`);
  }
  const month = keepMonth ? undefined : title.match(/(\d{1,2})월/)?.[1];
  if (month) said = said.replace(new RegExp(`^(?:(?:19|20)\\d{2}년\\s*)?${month}월\\s+`), '');
  // 「5곳에 분포」는 아무것도 말하지 않는다(9·10차 검토, 09:14 그림 결론에 다시 나왔다) — 「5곳」으로 줄인다.
  if (/분포/.test(said)) {
    said = said.replace(/(\d[\d,]*\s*(?:곳|개\s*\S+?))에\s*분포(?:함|됨|해\s*있음)?/g, '$1').replace(/(\d[\d,]*\s*곳)에(?=,)/g, '$1');
  }
  // 괄호 안에 같은 호기 수가 두 번이면 하나로(12차 검토: 「(가나본부 3개 현장 35호기, A 담당 35호기)」).
  said = said.replace(/\(([^()]*?)\s*(\d+)호기,\s*(\S+)\s*담당\s*\2호기\)/, (_, place: string, count: string, owner: string) => (
    `(${count}호기는 ${place.trim()}, 모두 ${owner} 담당)`));
  // 상태 낱말 뒤 끝 숫자에 단위가 빠졌으면 「건」(실측 13:06: 「…, 미실시 396」).
  said = said.replace(/((?:미실시|완료|계획|전체|대상|경과)\s*)(\d[\d,]*)$/, '$1$2건');
  // 「확인 필요(필수 여부 확인 필요)」처럼 끝을 되풀이한 괄호는 뗀다(2026-10-25 11:01).
  said = said.replace(/(확인\s*필요)\s*\(필수\s*여부\s*확인\s*필요\)$/, '$1');
  // em-dash와 「 · 」 나열은 쉼표로 바꾼다(사용자 전역 지침 stop-slop-ko). 붙임 복합어 「9·10월」은 그대로다.
  return said.replace(/\s*—\s*/g, ', ').replace(/ · /g, ', ');
}

/** 글 속 날짜('26.10.30.)를 「26.10.30」 꼴로. 둥근 따옴표(‘26.10.30.)도 같은 날짜다. */
function daysIn(text: string): string[] {
  return Array.from(text.replace(/[‘’`]/g, "'").matchAll(/'(\d{2})\.(\d{1,2})\.(\d{1,2})\./g),
    (found) => `${found[1]}.${Number(found[2])}.${Number(found[3])}`);
}

function countNumbers(text: string): number {
  return (text.replace(/(\d),(\d{3})/g, '$1$2').match(/\d+(?:\.\d+)?/g) ?? []).length;
}

/**
 * 줄의 값 숫자 수. 막대 이름 속 숫자(「2팀」「101동」)는 값이 아니라 세지 않는다(실측 2026-10-26, 부서 이름). 달 이름(「'26.9월」)은
 * 센다. 달 막대 값을 늘어놓은 줄이 그 덕에 걸리지 않게 됐다(독립 검토). 점검이 아닌 문서만 쓴다.
 */
function valueNumbers(line: string, charts: ReportChart[]): number {
  const names = Array.from(new Set(charts.flatMap((chart) => chart.categories)
    .map((category) => category.replace(/\s*\(.*\)\s*$/, '').trim())
    .filter((name) => /\d/.test(name) && name.length >= 2 && !TIME_CATEGORY.test(name))))
    .sort((left, right) => right.length - left.length);
  return countNumbers(names.reduce((text, name) => text.split(name).join(' '), line));
}

/**
 * 갈래 숫자를 쓴 줄마다 그 숫자를 센 칸 — 그 줄 바로 아래 * 줄로 쓴다(8차 검토: 문서 끝 ※ 묶음에서는 어느 줄의
 * 근거인지 안 보였다). 다른 근거로 설명되는 숫자는 갈래 몫이 아니다. shown 은 갈래 값의 비율을 붙인 줄이다.
 */
export function axisNotes(
  lines: string[],
  axes: ResultAxis[],
  grounds: unknown[],
): { line: string; shown: string; note: string; axis: ResultAxis }[] {
  const known = [{ axis: '근거', value: grounds }];
  return lines.flatMap((line) => {
    const stray = unsupportedNumbers(line, known);
    if (!stray.length) return [];
    // 그 줄의 숫자를 다 설명하는 갈래 중 말이 가장 겹치는 하나 — 작은 숫자는 여러 갈래에 함께 있어
    // 관계없는 갈래(현장·호기, 담당자)까지 붙었다(실제 테넌트, 2026-10-24).
    const words = new Set(pairsOf(line));
    const best = axes
      .filter((one) => stray.every((number) => !unsupportedNumbers(number, [{ axis: one.axis, value: axisText(one) }]).length))
      .map((one) => {
        const keys = one.value && typeof one.value === 'object' ? Object.keys(one.value as Record<string, unknown>).join(' ') : '';
        return { one, score: pairsOf(`${one.axis} ${one.columns.join(' ')} ${keys}`).filter((pair) => words.has(pair)).length };
      })
      .sort((left, right) => right.score - left.score)[0]?.one;
    const columns = best ? countedColumns(line, best) : [];
    return best && columns.length
      ? [{ line, shown: withShares(line, best, stray), note: axisNote(best.axis, columns), axis: best }]
      : [];
  });
}

type CountedColumn = { name: string; codes: string[] };

/**
 * 그 줄의 숫자를 센 칸: 줄에 나온 값 이름(대상아님)이나 칸 이름이 가장 길게 겹치는 칸. 통째로 겹치는 칸이 없으면 칸 이름의
 * 두 글자 묶음이 가장 많이 겹치는 칸(「고객 서명」의 「고객」은 고객싸인에만 있다, 13차 검토). 그것도 없으면 갈래의 칸 전부.
 * 칸 이름의 설명 꼬리(「, 예: 20261001」)는 떼고, 화면 이름이 없는 칸(sendflag)은 값 이름으로 부른다 — 「전송」은
 * 다른 두 칸의 화면 이름이었다(8차 검토). name 이 빈 칸은 화면 이름이 없는 칸이다.
 */
function countedColumns(line: string, axis: ResultAxis): CountedColumn[] {
  const flat = line.replace(/\s+/g, '');
  const columns = axis.columns.map((column) => {
    const raw = column.split(/[,(]/)[0]?.trim() ?? '';
    // 값 목록은 첫 「값:」 마디만 본다. 칸 설명 꼬리 「, 그 밖의 값: 1 4행」이 각주에 실렸다(2026-10-25 11:01).
    const codes = (column.match(/값:\s*([^,]+)/)?.[1] ?? '').split('·').map((code) => code.trim()).filter(Boolean);
    const name = /^[\x00-\x7F]*$/.test(raw) ? '' : raw;
    const hit = Math.max(0, ...[raw, ...codes].map((word) => word.replace(/\s+/g, ''))
      .filter((word) => word.length >= 2 && flat.indexOf(word) >= 0).map((word) => word.length));
    const near = name ? pairsOf(name).filter((pair) => flat.indexOf(pair) >= 0).length : 0;
    return { name, codes, hit, near };
  }).filter((column) => column.name || column.codes.length);
  const best = Math.max(0, ...columns.map((column) => column.hit));
  const nearest = best ? 0 : Math.max(0, ...columns.map((column) => column.near));
  const chosen = columns.filter((column) => (best ? column.hit === best : !nearest || column.near === nearest));
  return chosen.filter((column, index) => chosen.findIndex((one) => one.name === column.name
    && one.codes.join('·') === column.codes.join('·')) === index).map(({ name, codes }) => ({ name, codes }));
}

/**
 * 각주 — 「정보센터 전송 상태(미전송·전송·대상 아님) 기준」, 「서명(고객싸인) 기준」. 「집계」「칸」은 빼고
 * 값 이름은 띄어 쓴다(9차 검토: 「대상아님」 → 「대상 아님」).
 */
function axisNote(axis: string, columns: CountedColumn[]): string {
  const spaced = (code: string) => code.replace(/([가-힣])(아님|없음|있음)$/, '$1 $2');
  const [only] = columns;
  if (columns.length === 1 && only && !only.name) return `${axis} 상태(${only.codes.map(spaced).join('·')}) 기준`;
  const parts = columns.map((column) => column.name || `${column.codes.map(spaced).join('·')} 구분`);
  return parts.length === 1 && parts[0] === axis ? `${axis} 기준` : `${axis}(${parts.join('·')}) 기준`;
}

/** 갈래 값의 {건수, 비율(%)} 짝 — 건수마다 비율. */
function sharesOf(value: unknown, found = new Map<number, number[]>()): Map<number, number[]> {
  if (!value || typeof value !== 'object') return found;
  const entries = Array.isArray(value) ? value.map((one, index) => [String(index), one] as const)
    : Object.entries(value as Record<string, unknown>);
  const count = entries.find(([key, one]) => /건수/.test(key) && typeof one === 'number')?.[1];
  const share = entries.find(([key, one]) => /비율/.test(key) && typeof one === 'number')?.[1];
  if (typeof count === 'number' && typeof share === 'number') found.set(count, [...(found.get(count) ?? []), share]);
  entries.forEach(([, one]) => sharesOf(one, found));
  return found;
}

/**
 * 갈래 숫자에 비율이 없으면 갈래 값의 비율을 붙인다 — 「전송 대상 아님 106건」(8차 검토: 106건, 4.5%).
 * 그 갈래로만 설명되는 숫자에, 비율이 하나로 정해질 때만.
 */
function withShares(line: string, axis: ResultAxis, numbers: string[]): string {
  const shares = sharesOf(axis.value);
  return line.replace(/(\d[\d,]*)건(?!\s*\()/g, (whole, digits: string) => {
    const count = Number(digits.replace(/,/g, ''));
    const list = numbers.indexOf(String(count)) >= 0 ? shares.get(count) ?? [] : [];
    return list.length && list.every((one) => one === list[0]) ? `${whole}(${list[0]}%)` : whole;
  });
}

/**
 * 그림 결론은 그 그림의 값·합계로 대조한다 — 그림 2 의 결론을 그림 1 자리에 썼다(실제 테넌트, 2026-10-24).
 * 제 그림 값으로 설명되지 않는데 다른 그림 하나로 설명되고 그 자리가 비었으면 옮기고, 아니면 뺀다.
 */
function placed(lines: string[], charts: ReportChart[], totals = false): string[] {
  // 그림 값·합계와 계열 안의 비율(구성 그림의 79.3%)까지가 그 그림의 숫자다.
  const shares = (chart: ReportChart) => chart.series.map((series) => {
    const total = series.values.reduce<number>((sum, value) => sum + (value ?? 0), 0);
    return total ? series.values.map((value) => Math.round(((value ?? 0) / total) * 1000) / 10) : [];
  });
  const fits = (line: string, chart: ReportChart) => !unsupportedNumbers(line,
    [{ axis: chart.title, value: [chart.series, chartText([chart], totals), shares(chart)] }]).length;
  const out = lines.map((line, index) => (line && charts[index] && fits(line, charts[index] as ReportChart) ? line : ''));
  lines.forEach((line, index) => {
    if (!line || out[index]) return;
    const home = charts.findIndex((chart, at) => at !== index && !out[at] && !lines[at] && fits(line, chart));
    if (home >= 0) out[home] = line;
  });
  return out;
}

/**
 * 조치의 몸(괄호 속 기한·담당을 뺀 것)이 거의 다 다른 줄에 들어 있는가. 날짜는 한 꼴로 맞춰 견준다 — 확인 사항의
 * 「10월 23~24일 예정 8건」이 조치의 「'26.10.23.~'26.10.24. 예정 8건」과 같은 일인데 두 칸에 나왔다(2026-10-25 09:14).
 */
function covered(action: string, line: string): boolean {
  const body = pairsOf(dayForms(action).replace(/\([^)]*\)/g, ''));
  const pool = new Set(pairsOf(dayForms(line)));
  return body.length > 0 && body.filter((pair) => pool.has(pair)).length / body.length >= 0.75;
}

/** 날짜 꼴을 「10월 23~24일」로 — 견주기만 한다(문서 글은 바꾸지 않는다). */
function dayForms(text: string): string {
  return text
    .replace(/'\d{2}\.(\d{1,2})\.(\d{1,2})\.\s*~\s*'\d{2}\.(\d{1,2})\.(\d{1,2})\./g, (_, month: string, from: string, other: string, to: string) => (
      month === other ? `${month}월 ${from}~${to}일` : `${month}월 ${from}일~${other}월 ${to}일`))
    .replace(/'\d{2}\.(\d{1,2})\.(\d{1,2})\./g, '$1월 $2일')
    .replace(/(\d{1,2})월\s*(\d{1,2})일\s*~\s*(\d{1,2})일/g, '$1월 $2~$3일');
}

/**
 * 조치와 같은 일을 적은 확인 사항을 뺀다 — 조치가 다 정해진 뒤(코드가 넣은 묶음별 조치까지) 한 번 더 본다.
 * 같은 8건이 확인 필요 사항과 향후 조치에 함께 나왔다(2026-10-25 09:14).
 */
export function uncovered(checks: string[], actions: string[]): string[] {
  return checks.filter((item) => !actions.some((action) => covered(action, item)));
}

/** 글자 두 개씩 묶음 — 띄어쓰기·기호는 뺀다. */
function pairsOf(text: string): string[] {
  const plain = text.replace(/[\s·,.()%]/g, '');
  return Array.from({ length: Math.max(plain.length - 1, 0) }, (_, index) => plain.slice(index, index + 2));
}

/**
 * 같은 말인가 — 숫자 둘 이상이 모두 상대에 있거나, 글자 묶음이 60% 넘게 겹친다.
 * 「미조회로 이상 여부 판단 불가」와 「미조회로 적합·부적합 여부 판단 불가」는 같은 말이다.
 */
function repeats(line: string, other: string): boolean {
  const numbers = (text: string): string[] => text.replace(/(\d),(\d{3})/g, '$1$2').match(/\d+(?:\.\d+)?/g) ?? [];
  const mine = numbers(line);
  const theirs = numbers(other);
  if (mine.length >= 2 && mine.every((number) => theirs.indexOf(number) >= 0)) return true;
  const left = pairsOf(line);
  const right = pairsOf(other);
  const pool = [...right];
  const common = left.filter((pair) => {
    const at = pool.indexOf(pair);
    if (at < 0) return false;
    pool.splice(at, 1);
    return true;
  }).length;
  return left.length + right.length > 0 && (2 * common) / (left.length + right.length) > 0.6;
}

/** 한 번 쓰게 하고 칸마다 검사한다. 못 쓴 항목을 함께 돌려준다 — 다시 쓰게 할 때 그 까닭으로 쓴다. */
async function attempt(
  prompt: string,
  input: {
    charts: ReportChart[]; chat: Chat; stated?: string[]; months?: number[]; totals?: number[];
    done?: { label: string; count: number }[]; kind?: BriefKind; facts?: string[]; asked?: string;
    titleMonths?: number[]; forbidden?: string[];
  },
  grounds: unknown[],
): Promise<{ brief?: Brief; rejected: string[]; summaryLost?: boolean; actionsLost?: boolean; actions?: string[] }> {
  let raw: string;
  try {
    raw = await input.chat(prompt, { step: 'answer' });
  } catch {
    return { rejected: [] };
  }
  const json = raw.match(/\{[\s\S]*\}/)?.[0];
  if (!json) return { rejected: ['JSON 이 아니다'] };
  let value: Record<string, unknown>;
  try {
    value = JSON.parse(json) as Record<string, unknown>;
  } catch {
    return { rejected: ['JSON 이 아니다'] };
  }
  const rejected: string[] = [];
  // 근거에 경과·예정 구분이 있는데 「미집계」라 쓰면 일을 안 한 것으로 읽힌다(국가직 팀장 2차 검토, 2026-10-23).
  const split = JSON.stringify(grounds).indexOf('예정일 경과') >= 0;
  // 점검이 아닌 문서(목록 보고·공지)의 날짜는 근거에 있는 날짜(기한 후보 등)만, 제목의 달은 사용자가 말했거나 표에 든 달만.
  // 「2026년 10월 … 안내」, 「'26.10.31. 기준」이 나갔다. 날짜의 숫자 하나하나가 기한 후보에 들어 있어 통과했다(실측 2026-10-26 14:35).
  const strict = !!input.kind && input.kind !== 'status';
  const knownDays = new Set(daysIn((input.facts ?? []).join('\n')));
  const saidMonths = new Set(input.titleMonths ?? [...monthsIn(input.asked ?? ''), ...(input.months ?? [])]);
  const forbidden = (input.forbidden ?? []).filter((name) => name.length >= 2);
  // 공지는 경어 문장이다. 보고 문서의 개조식 검사로 보면 「~입니다」, 「~바랍니다」가 모두 버려진다.
  // 열린 문제 문서는 점검 미실시의 말(미실시, 예정일 경과, 점검 완료)도 버린다.
  const lint = input.kind === 'notice' ? noticeLint : input.kind === 'issues' ? issueLint : reportLint;
  const usable = (raw: unknown) => {
    if (typeof raw !== 'string' || !raw.trim()) return '';
    const text = tidy(raw, typeof value.title === 'string' ? value.title : '', (input.months?.length ?? 0) > 1, strict);
    // 공지에 개인 이름을 쓴 줄은 버린다. 다시 쓰게 할 때도 그 줄은 옮기지 않는다(이름이 지시문에 다시 실린다). 두 글자 이름은
    // 낱말 속(「10일 이상 보유자」)에서는 이름으로 보지 않는다(독립 재검토).
    if (namesIn(text, forbidden)) {
      rejected.push('(개인 이름을 쓴 줄을 뺐다. 공지에는 개인 이름을 쓰지 않는다)');
      return '';
    }
    if (split && /미집계|구분\s*미/.test(text)) {
      rejected.push(text.trim());
      return '';
    }
    // 문서의 달 밖 기간을 말하는 줄 — 10월 판에 「2026년 8~10월 조회분 결과일자 없음 457건」이 실렸다(2026-10-25 08:54).
    const allowed = input.months ?? [];
    if (allowed.length && monthsIn(text).some((month) => allowed.indexOf(month) < 0)) {
      rejected.push(text.trim());
      return '';
    }
    // 점검 대상·전체 건수는 〈표 1〉의 수로만 쓴다. 기준이 다른 갈래 값(25일까지 자른 2,009건)이 같은 이름으로 실렸다(10:23).
    const total = text.replace(/(\d),(\d{3})/g, '$1$2').match(/(?:점검\s*대상|전체)\s*(\d+)\s*건/)?.[1];
    if (total && input.totals?.length && input.totals.indexOf(Number(total)) < 0) {
      rejected.push(`${text.trim()} (점검 대상·전체 건수는 〈표 1〉의 수로 쓴다)`);
      return '';
    }
    // 「○○ 없음 N건」이 그 달 끝낸 건보다 많으면 아직 안 한 행이 섞였다(10:51: 10월 고객 서명 없음 2,206건, 완료 1,952건).
    const missing = text.replace(/(\d),(\d{3})/g, '$1$2').match(/없음\s*(\d+)\s*건/)?.[1];
    const done = input.done ?? [];
    const said = monthsIn(text);
    const row = said.length ? done.find((one) => said.indexOf(Number(one.label.match(/\.(\d{1,2})월/)?.[1] ?? 0)) >= 0)
      : done.length === 1 ? done[0] : undefined;
    if (missing && row && Number(missing) > row.count) {
      rejected.push(`${text.trim()} (없음 건수가 그 달 끝낸 ${row.count.toLocaleString('en-US')}건보다 많다. 끝낸 건 중에서 센 수로 쓴다)`);
      return '';
    }
    const strayDays = strict ? daysIn(text).filter((day) => !knownDays.has(day)) : [];
    if (strayDays.length) {
      rejected.push(`${text.trim()} (근거에 없는 날짜다. 기한은 기한 후보에서 고르고, 기준일은 문서 머리에 있다)`);
      return '';
    }
    if (lint(text) && grounded(text.trim(), grounds)) return text.trim();
    rejected.push(text.trim());
    return '';
  };
  const items = (list: unknown) => (Array.isArray(list) ? list.map(usable).filter(Boolean) : []);
  // 제목은 손질하지 않는다(제목의 달은 그대로) — 항목 앞의 달만 뺀다.
  const rawTitle = typeof value.title === 'string' ? value.title.trim() : '';
  const titleMonths = strict ? monthsIn(rawTitle).filter((month) => !saidMonths.has(month)) : [];
  // 공지 제목에 개인 이름을 썼으면 버리고, 다시 쓰게 할 때 그 제목을 옮기지 않는다.
  const titleNamed = namesIn(rawTitle, forbidden);
  const title = rawTitle && !titleNamed && !titleMonths.length && lint(rawTitle) && grounded(rawTitle, grounds) ? rawTitle : '';
  if (rawTitle && !title) {
    rejected.push(titleNamed ? '(개인 이름을 쓴 제목을 뺐다. 공지에는 개인 이름을 쓰지 않는다)'
      : titleMonths.length ? `${rawTitle} (제목의 달은 질문이나 자료에 없는 달이다)` : rawTitle);
  }
  const before = rejected.length;
  const listed = items(value.summary);
  // 요약 첫 줄은 숫자가 든 결론이다. 「자체점검은 진행 중임」이 첫 줄이었다(4차 검토). 아니면 다시 쓰게 한다. 공지의 첫 줄은
  // 무엇을 왜 안내하는지라 숫자가 없어도 된다(독립 검토: 두 번 다 이 검사에 걸려 공지 글이 없어졌다).
  const firstEmpty = input.kind !== 'notice' && listed.length > 0 && !/\d/.test(listed[0] ?? '');
  if (firstEmpty) rejected.push(`${listed[0]} (요약 첫 줄에 숫자가 든 결론이 없다)`);
  const summary = firstEmpty ? listed.slice(1) : listed;
  const summaryLost = rejected.length > before;
  // 요약 줄을 되풀이한 확인 사항은 뺀다 — 「판단 불가」가 두 번 나와 발표자료에 그 한 줄뿐인 장이 생겼다(5차 검토).
  // 조치가 「~함」으로 끝나면 끝난 일처럼 읽힌다(4차 검토) — 버린다.
  const actions = items(value.actions).filter((item) => {
    if (!/함\.?$/.test(item)) return true;
    rejected.push(`${item} (조치는 명사형으로 끝낸다)`);
    return false;
  });
  // 점검이 아닌 문서의 조치·요청에 기한이 하나도 없으면 한 번 다시 쓰게 한다. 다시 써도 없으면 그대로 둔다
  // (실측 2026-10-26 15:05 발표자료: 「잔여 연차 보유자 잔여 일수 확인 및 사용 계획 수립」처럼 기한·담당 없는 조치 두 줄).
  const undated = strict && actions.length > 0 && actions.every((item) => !daysIn(item).length);
  if (undated) actions.forEach((item) => rejected.push(`${item} (기한이 없다. 기한 후보에서 기한과 담당을 붙인다)`));
  // 조치를 썼는데 모두 버려졌을 때도 다시 쓰게 한다. 요청 사항 없는 공지가 나갔다(실측 2026-10-26 16:40). 공지는 요청 사항이
  // 핵심이라 아예 안 썼어도 다시 쓰게 한다(실측 17:35).
  const wrote = Array.isArray(value.actions) && value.actions.some((one) => typeof one === 'string' && one.trim());
  const noRequest = input.kind === 'notice' && !wrote;
  if (noRequest) rejected.push('(요청 사항이 없다. 직원이나 부서장이 할 일과 기한을 기한 후보에서 골라 1~3항목 쓴다)');
  // 기한 붙은 조치가 둘 이상인데 기한이 모두 같으면 한 번 다시 쓰게 한다. 연차를 12월 31일까지 쓰라면서 신청 기한도 같은 날로
  // 두면 그 기한은 지킬 수 없다(국가직 팀장 검토, 연차 발표자료).
  const dated = actions.map(daysIn).filter((days) => days.length);
  const oneDay = strict && dated.length >= 2 && new Set(dated.flat()).size === 1;
  if (oneDay) rejected.push('(조치의 기한이 모두 같다. 앞 단계는 이른 기한, 마지막 단계는 늦은 기한으로 기한 후보에서 고른다)');
  const actionsLost = undated || oneDay || (strict && wrote && !actions.length) || noRequest;
  // 조치를 되풀이한 확인 사항도 뺀다 — 같은 7건이 두 칸에 나와 PDF 가 두 쪽이 됐다(실제 테넌트, 2026-10-24).
  // 문서에 이미 쓴 쏠림 줄을 되풀이한 확인 사항도 뺀다. 그 한 줄로 두 장이 됐다(실측 2026-10-26 07:03, 1,157px).
  const checks = items(value.checks).filter((item) => !summary.some((line) => repeats(item, line))
    && !actions.some((action) => covered(action, item))
    && !(input.stated ?? []).some((stated) => repeats(item, stated)));
  const written = Array.isArray(value.charts) && input.charts.length
    ? input.charts.map((_, index) => usable((value.charts as unknown[])[index]))
      // 숫자 없는 그림 결론(「…형태로 구분됨」)은 결론이 아니다(2026-10-24 실측) — 빼고 그림 제목만 둔다.
      // 숫자를 다섯 개 넘게 늘어놓은 줄은 결론이 아니라 막대 값의 되풀이다(7차 검토).
      .map((line) => (line && (!/\d/.test(line) || (strict ? valueNumbers(line, input.charts) : countNumbers(line)) > 5
        || (input.stated ?? []).some((stated) => repeats(line, stated))) ? '' : line))
    : [];
  const charts = placed(written, input.charts, strict);
  if (rejected.length) {
    // eslint-disable-next-line no-console
    console.debug('[agent] 보고 문서 항목 버림', rejected.join(' | ').slice(0, 400));
  }
  // 제목이나 요약 때문에 글을 못 쓰게 돼도 쓸 수 있는 조치는 넘긴다. 다시 쓴 글이 조치를 잃으면 이것을 잇는다(실측 17:35).
  if (!title || !summary.length) return { rejected: rejected.length ? rejected : ['제목·요약이 비었다'], actions };
  return {
    brief: { title, summary, checks, actions, ...(charts.some(Boolean) ? { charts } : {}) },
    rejected,
    summaryLost,
    actionsLost,
  };
}
